import { useEffect, useMemo, useRef, useState } from "react";
import * as echarts from "echarts/core";
import { HeatmapChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  VisualMapComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import {
  useDashboardHeatmap,
  useHeatmapFilterOptions,
  useHeatmapOptions,
  type HeatmapDirection,
  type HeatmapMatrix,
  type HeatmapMetric,
  type HeatmapValueKind,
} from "@/api/dashboard";
import { titleCase } from "@/utils/format";

echarts.use([
  HeatmapChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  VisualMapComponent,
  CanvasRenderer,
]);

/**
 * Two-dimensional view of residential stock, with a choice of CELL VALUE.
 *
 * The panel used to answer exactly one question — "how much stock is
 * there" — on four axis pairings, because the cell was always a count.
 * An investor's questions are about price, liquidity and return, and
 * those are a different value over the same axes rather than a different
 * chart (MPPI-00855). Six metrics over six matrices is 36 readings, and
 * the pairings that matter most are the ones a count could not express:
 * **Community × Time on market** coloured by median AED/sqft is the
 * platform's own premise on one screen — stock that has sat for months
 * while asking more than its neighbours.
 *
 * **The population is secondary + ready-to-move stock only** — 62,990 of
 * 136,365 active residential properties (MPPI-00856). Off-plan cannot be
 * bought from an owner, and developer primary stock asks 2.3x less per
 * square foot and records a price cut on a tenth as many adverts, so
 * including it made every median and every share read lower than the
 * market it was labelled as. The section title says so, because a
 * narrowed population that is not announced is just a wrong number.
 *
 * Three further things here are about honesty rather than presentation.
 *
 * **The sample travels with the value.** Every tooltip states how many
 * properties the median came from, and the server withholds any cell
 * under ten rather than returning a number. A median over four
 * properties IS four properties, and on a colour ramp a thin cell reads
 * as the strongest signal on the chart.
 *
 * **The scale is taken from the data, not from zero.** Median AED/sqft
 * runs 400–7,900 across cells; anchoring the ramp at 0 would render the
 * entire market one shade of the same colour and hide every difference
 * the panel exists to show.
 *
 * **Colour direction comes from the server.** Cheaper per sqft is good
 * for a buyer and a higher yield is good; more days on market is
 * genuinely ambiguous — more room to negotiate, harder to exit — so that
 * one is deliberately rendered on a magnitude-only ramp instead of being
 * coloured as if it were a verdict.
 */

/** Ramps by what the number MEANS, not by magnitude alone. */
const RAMPS: Record<HeatmapDirection, string[]> = {
  // Red = unfavourable, green = favourable.
  high_good: ["#dc2626", "#fbbf24", "#16a34a"],
  high_bad: ["#16a34a", "#fbbf24", "#dc2626"],
  // Magnitude only — no claim about which end is better.
  neutral: ["#fef3c7", "#fbbf24", "#b91c1c"],
};

function formatValue(value: number, kind: HeatmapValueKind): string {
  switch (kind) {
    case "count":
      return value >= 1000
        ? `${Math.round(value / 100) / 10}k`
        : String(Math.round(value));
    case "aed_sqft":
      return value >= 1000
        ? `${Math.round(value / 100) / 10}k`
        : String(Math.round(value));
    case "pct":
      return `${value.toFixed(1)}%`;
    case "days":
      return String(Math.round(value));
    case "score":
      return value.toFixed(1);
  }
}

function formatValueLong(value: number, kind: HeatmapValueKind): string {
  switch (kind) {
    case "count":
      return `${value.toLocaleString()} ${value === 1 ? "property" : "properties"}`;
    case "aed_sqft":
      return `AED ${Math.round(value).toLocaleString()} / sqft`;
    case "pct":
      return `${value.toFixed(1)}%`;
    case "days":
      return `${Math.round(value).toLocaleString()} days`;
    case "score":
      return value.toFixed(1);
  }
}

export function HeatmapPanel() {
  const ref = useRef<HTMLDivElement>(null);
  // Defaults keep the panel's original reading on first load: bedrooms
  // against asking price, cells counted.
  const [matrix, setMatrix] = useState<HeatmapMatrix>("beds_x_price");
  const [metric, setMetric] = useState<HeatmapMetric>("count");
  const [emirate, setEmirate] = useState<string>("");
  const [community, setCommunity] = useState<string>("");

  const { data: catalogue } = useHeatmapOptions();
  const { data, isLoading } = useDashboardHeatmap(matrix, metric, {
    emirate: emirate || null,
    community: community || null,
  });
  const { data: options } = useHeatmapFilterOptions();

  // Communities are scoped to the chosen emirate: offering all 222 Dubai
  // communities while "Sharjah" is selected would produce empty charts.
  const communityOptions = (options?.communities ?? []).filter(
    (c) => !emirate || c.emirate === emirate,
  );

  // [col,row] → properties, so the tooltip can state the sample behind a
  // median without a linear scan per cell.
  const sampleAt = useMemo(() => {
    const m = new Map<string, number>();
    (data?.sample ?? []).forEach(([c, r, n]) => m.set(`${c}:${r}`, n));
    return m;
  }, [data]);

  useEffect(() => {
    if (!ref.current) return;
    const chart =
      echarts.getInstanceByDom(ref.current) ?? echarts.init(ref.current);

    if (!data || !data.rows.length || !data.cols.length) {
      chart.clear();
      return;
    }

    const kind = data.value_kind;
    const colLabels = data.cols.map((c) => titleCase(String(c)));
    const rowLabels = data.rows.map((r) => titleCase(String(r)));
    const values = data.data.map(([, , v]) => v);

    // A count ramp starts at zero because zero is meaningful there. A
    // median does not: median AED/sqft spans 400–7,900 across cells, and
    // anchoring at 0 paints the whole market one colour.
    const dataMin = values.length ? Math.min(...values) : 0;
    const dataMax = values.length ? Math.max(...values) : 1;
    const rampMin = kind === "count" ? 0 : dataMin;
    const rampMax = dataMax > rampMin ? dataMax : rampMin + 1;
    const wideAxis = matrix.startsWith("community");

    chart.setOption(
      {
        title: {
          text: data.metric_label,
          subtext: `${data.row_label} × ${data.col_label}`,
          left: "center",
          textStyle: { fontSize: 13 },
          subtextStyle: { fontSize: 10 },
        },
        tooltip: {
          position: "top",
          formatter: (p: unknown) => {
            const obj = p as { value: [number, number, number] };
            const [c, r, v] = obj.value;
            const n = sampleAt.get(`${c}:${r}`);
            const sampleLine =
              kind === "count" || n === undefined
                ? ""
                : `<br/><span style="color:#6b7280">from ${n.toLocaleString()} ${
                    n === 1 ? "property" : "properties"
                  }</span>`;
            return (
              `<b>${rowLabels[r]}</b> · ${colLabels[c]}<br/>` +
              `${formatValueLong(v, kind)}${sampleLine}`
            );
          },
        },
        grid: { left: wideAxis ? 140 : 80, right: 70, top: 56, bottom: 62 },
        xAxis: {
          type: "category",
          data: colLabels,
          name: data.col_label,
          nameLocation: "middle",
          nameGap: 32,
          splitArea: { show: true },
          axisLabel: { fontSize: 9, interval: 0 },
        },
        yAxis: {
          type: "category",
          data: rowLabels,
          name: data.row_label,
          nameLocation: "middle",
          nameGap: wideAxis ? 120 : 60,
          splitArea: { show: true },
          axisLabel: { fontSize: 9 },
          inverse: wideAxis,
        },
        visualMap: {
          min: rampMin,
          max: rampMax,
          right: 5,
          bottom: 30,
          calculable: true,
          textStyle: { fontSize: 9 },
          formatter: (v: number) => formatValue(v, kind),
          inRange: { color: RAMPS[data.direction] },
        },
        series: [
          {
            type: "heatmap",
            data: data.data,
            label: {
              show: true,
              fontSize: 9,
              color: "#1f2937",
              formatter: (p: unknown) => {
                const obj = p as { value: [number, number, number] };
                const v = obj.value[2];
                if (kind === "count" && v === 0) return "";
                return formatValue(v, kind);
              },
            },
            emphasis: {
              itemStyle: { shadowBlur: 6, shadowColor: "rgba(0,0,0,0.4)" },
            },
          },
        ],
      },
      // Replace rather than merge: switching metric changes the ramp, the
      // axis names and the label formatter, and a merged option would
      // keep the previous metric's visualMap bounds.
      { notMerge: true },
    );
    const onResize = () => chart.resize();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [data, matrix, metric, emirate, community, sampleAt]);

  // Dispose on unmount only — keep the instance alive across data
  // changes so transitions are smooth.
  useEffect(() => {
    return () => {
      if (ref.current) {
        const inst = echarts.getInstanceByDom(ref.current);
        inst?.dispose();
      }
    };
  }, []);

  const matrices = catalogue?.matrices ?? [];
  const metrics = catalogue?.metrics ?? [];

  return (
    <div className="rounded-lg border bg-white p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="sr-only">Market heatmap</h3>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1 text-[11px] text-gray-500">
            Show
            <select
              value={metric}
              onChange={(e) => setMetric(e.target.value as HeatmapMetric)}
              className="rounded border-gray-300 text-xs"
            >
              {metrics.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1 text-[11px] text-gray-500">
            by
            <select
              value={matrix}
              onChange={(e) => setMatrix(e.target.value as HeatmapMatrix)}
              className="rounded border-gray-300 text-xs"
            >
              {matrices.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="mb-2 flex flex-wrap items-center gap-2">
        <select
          value={emirate}
          onChange={(e) => {
            setEmirate(e.target.value);
            // The chosen community may not exist in the new emirate.
            setCommunity("");
          }}
          className="rounded border-gray-300 text-xs"
        >
          <option value="">All emirates</option>
          {(options?.emirates ?? []).map((em) => (
            <option key={em} value={em}>
              {em}
            </option>
          ))}
        </select>

        <select
          value={community}
          onChange={(e) => setCommunity(e.target.value)}
          className="max-w-[16rem] rounded border-gray-300 text-xs"
        >
          <option value="">All communities</option>
          {communityOptions.map((c) => (
            <option key={`${c.emirate}-${c.name}`} value={c.name}>
              {titleCase(c.name)} ({c.count.toLocaleString()})
            </option>
          ))}
        </select>

        {(emirate || community) && (
          <button
            type="button"
            onClick={() => {
              setEmirate("");
              setCommunity("");
            }}
            className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
          >
            Clear
          </button>
        )}
        {(emirate || community) && (
          <span className="text-[11px] text-gray-500">
            Filtered to {community ? titleCase(community) : emirate}
          </span>
        )}
      </div>

      <div ref={ref} className="h-80 w-full" />

      {/* An empty result under a filter is a real answer, not a failure —
          say which, rather than leaving a blank canvas. */}
      {!isLoading && data && data.data.length === 0 && (
        <p className="text-center text-xs text-gray-500">
          No properties match{" "}
          {community ? titleCase(community) : emirate || "this selection"}.
        </p>
      )}
      {isLoading && (
        <p className="text-center text-xs text-gray-400">Loading…</p>
      )}

      {/* A blank cell has two causes and the reader must be able to tell
          them apart: no stock at all, or too little to quote a median
          from. Saying so is the difference between an honest gap and a
          chart that looks broken. */}
      {data && data.data.length > 0 && (
        <p className="mt-1 text-[11px] leading-snug text-gray-500">
          Secondary and ready-to-move stock only — off-plan and developer
          primary inventory excluded. One row per property, from its primary
          advert.
          {data.min_sample > 0 && (
            <>
              {" "}
              Blank cells hold fewer than {data.min_sample} properties — too
              few to quote a median from
              {data.cells_withheld > 0
                ? `, which is ${data.cells_withheld} cell${
                    data.cells_withheld === 1 ? "" : "s"
                  } here`
                : ""}
              .
            </>
          )}
          {metric === "median_ppsf" ||
          metric === "pct_price_cut" ||
          metric === "median_gross_yield" ? (
            <> Priced readings use portal listings only.</>
          ) : null}
        </p>
      )}
    </div>
  );
}
